import { useEffect, useState } from "react";
import { Eye, Users, TrendingUp, MousePointerClick, Loader2 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import api from "@/lib/api";
import DashLayout from "@/components/xuma/DashLayout";

const CLICK_LABELS = {
  whatsapp: "WhatsApp", phone: "Llamadas", email: "Correo",
  website: "Sitio web", save: "Guardar", share: "Compartir", link: "Enlaces",
};
const STATUS_LABELS = {
  nuevo: "Nuevo", contactado: "Contactado", en_seguimiento: "Seguimiento",
  cita: "Cita", ganado: "Ganado", perdido: "Perdido",
};
const STATUS_COLORS = {
  nuevo: "#F4A261", contactado: "#2563EB", en_seguimiento: "#6D28D9",
  cita: "#0891B2", ganado: "#16794D", perdido: "#B42318",
};

export default function Stats() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats").then(({ data }) => setStats(data)).catch(() => setStats(null)); }, []);

  if (!stats) {
    return <DashLayout title="Estadísticas"><div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-xuma-primary" /></div></DashLayout>;
  }

  const kpis = [
    { icon: Eye, label: "Vistas de tarjeta", value: stats.views },
    { icon: Users, label: "Prospectos", value: stats.total_leads },
    { icon: TrendingUp, label: "Conversión", value: `${stats.conversion}%` },
    { icon: MousePointerClick, label: "Clientes ganados", value: stats.by_status?.ganado || 0 },
  ];

  const clickData = Object.entries(CLICK_LABELS)
    .map(([k, label]) => ({ name: label, value: (stats.clicks || {})[k] || 0 }))
    .filter((d) => d.value > 0);

  const pipelineData = Object.entries(STATUS_LABELS)
    .map(([k, label]) => ({ name: label, value: (stats.by_status || {})[k] || 0, color: STATUS_COLORS[k] }));

  return (
    <DashLayout title="Estadísticas">
      {!stats.published && (
        <div className="mb-6 rounded-2xl bg-xuma-accent/10 border border-xuma-accent/30 p-4 text-sm text-xuma-dark">
          Publica tu tarjeta para empezar a registrar visitas y prospectos.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} data-testid={`kpi-${k.label}`} className="rounded-2xl bg-white border border-xuma-border p-6">
            <k.icon className="h-5 w-5 text-xuma-primary" />
            <p className="mt-4 font-heading font-extrabold text-3xl text-xuma-dark">{k.value}</p>
            <p className="text-sm text-xuma-muted">{k.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white border border-xuma-border p-6">
          <h3 className="font-heading font-bold text-lg text-xuma-dark mb-1">Interacciones por botón</h3>
          <p className="text-sm text-xuma-muted mb-4">Clics en las acciones de tu tarjeta.</p>
          {clickData.length === 0 ? (
            <p className="text-sm text-xuma-muted py-12 text-center">Sin interacciones todavía.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={clickData}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#57534E" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#57534E" />
                <Tooltip cursor={{ fill: "#F5F5F4" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#E25A38" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-2xl bg-white border border-xuma-border p-6">
          <h3 className="font-heading font-bold text-lg text-xuma-dark mb-1">Pipeline de prospectos</h3>
          <p className="text-sm text-xuma-muted mb-4">Cómo avanzan tus oportunidades.</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={pipelineData} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} stroke="#57534E" />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12 }} stroke="#57534E" />
              <Tooltip cursor={{ fill: "#F5F5F4" }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {pipelineData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </DashLayout>
  );
}
